'use client';
import { gsap } from 'gsap';
import { useRef, useEffect, useState } from 'react';
import Section from '@repo/ui/src/components/Section';
import { BackgroundStyleEnum } from '@repo/ui/src/enums/shared-enums';
import CascadingCard from './_CascadingCard';
import { useIsMobile, useReducedMotion } from '@repo/ui/src/utils/useMediaQuery';
import { classNames } from '@repo/ui/src/utils/classNames';

interface CascadingCardsProps {
  staticContent?: {
    author: {
      heading: string;
      link: string;
    };
    story: {
      heading: string;
      link: string;
      playButtonLabel: string;
    };
    recipe: {
      by: string;
      cookTimeLabel: string;
      effort: string;
      heading: string;
      link: string;
      prepTimeLabel: string;
    };
  };
  _data: any;
  backgroundStyle: any;
  bottomPadding?: boolean;
  _testID: string;
}

const CascadingCards = ({
  _data,
  staticContent,
  backgroundStyle,
  bottomPadding = false,
  _testID,
}: CascadingCardsProps) => {
  const ref = useRef<HTMLElement>(null);
  const [isClient, setIsClient] = useState(false);

  const isMobile = useIsMobile();
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    setIsClient(true);
  }, []);

  const yTransition = reduceMotion || isMobile ? 0 : 20;

  useEffect(() => {
    if (!isClient) return;
    const element = ref.current;
    //@ts-expect-error gsap
    const timeline = new gsap.timeline({
      scrollTrigger: {
        trigger: element,
        start: 'top center',
        end: 'bottom bottom',
        scrub: 0.5,
      },
    });

    timeline.addLabel('entry');

    _data?.forEach((_: any, i: any) => {
      const card = element?.querySelector(`.card-${i}`);
      if (card) {
        timeline.from(card, {
          yPercent: yTransition,
          opacity: 0,
        });
      }
    });
  }, [isClient, yTransition, _data]);

  if (_data?.filter((x: any) => !!x)?.length < 3) return null;

  return (
    <Section
      white={backgroundStyle === BackgroundStyleEnum.White}
      texture={
        backgroundStyle === BackgroundStyleEnum.TextureLight ||
        backgroundStyle === BackgroundStyleEnum.TextureDark
      }
      dark={
        backgroundStyle === BackgroundStyleEnum.TextureDark ||
        backgroundStyle === BackgroundStyleEnum.ButcherPaper
      }
      butcherPaper={backgroundStyle === BackgroundStyleEnum.ButcherPaper}
      gutterX
      gutterY
      className={classNames(!bottomPadding && '!tw-pb-0')}
      as="section"
      rootRef={ref}
    >
      {_data?.map((card: any, i: number) => (
        <CascadingCard
          key={i}
          i={i}
          card={card}
          staticContent={staticContent}
          className={`card-${i}`}
          _testID={_testID}
        />
      ))}
    </Section>
  );
};

export default CascadingCards;